/**
 * La bozza di un registro: creazione e configurazione (CF08/D08) — CL05.
 *
 * Il registro organizzazione si amministra dalle impostazioni, quello locale
 * dalla master del modello: la sola differenza fra i due è **dove nasce**, e
 * sta nello scope. Tutto il resto — pattern, iniziale, reset annuale, stato —
 * è la stessa cosa, quindi vive qui una volta.
 *
 * Due regole che questo modulo esiste per non sbagliare:
 *
 * - **In creazione si dichiara lo scope; in modifica no.** Lo scope di un
 *   registro non cambia: spostarlo da locale a organizzazione (o viceversa)
 *   cambierebbe chi condivide il contatore, e i numeri già emessi resterebbero
 *   a dire il contrario.
 * - **Il `code` si manda solo in creazione.** È l'identità con cui i codici
 *   emessi sono stati costruiti: cambiarlo li renderebbe irriconoscibili.
 *   Il server lo ignora in modifica, e mandarlo lo stesso sarebbe una bugia
 *   scritta nel payload.
 */

import type {
  RegistryConfiguration,
  RegistryMutation,
  RegistryRead,
  RegistryScope,
  SequencePattern,
} from "model/CustomFields";
import { PATTERN_LIMITS, registryConfigIssues } from "./sequences";

/** Il pattern iniziale usa solo il numero: `001`. */
export const DEFAULT_PATTERN: SequencePattern = {
  prefix: "",
  // Nessun separatore finché non c'è un segmento da separare: il numero da
  // solo non ne ha bisogno, e "-" era una scelta che l'utente non aveva fatto.
  separator: "",
  year: "none",
  padding: 3,
  postfix: "",
};

/** Removes disabled segments from the saved pattern without changing the edit draft. */
export function configurationWithOptionalSegments(
  configuration: RegistryConfiguration,
  enabled: { prefix: boolean; postfix: boolean; year: boolean }
): RegistryConfiguration {
  return {
    ...configuration,
    pattern: {
      ...configuration.pattern,
      prefix: enabled.prefix ? configuration.pattern.prefix : "",
      postfix: enabled.postfix ? configuration.pattern.postfix : "",
      year: enabled.year ? configuration.pattern.year : "none",
    },
    annualReset: enabled.year ? configuration.annualReset : false,
  };
}

/**
 * Una configurazione da cui partire.
 *
 * L'iniziale è 1 e non 0: il cursore parte uno sotto il primo codice, quindi
 * zero lo metterebbe a -1. La spec lo dice (D11) e il contratto lo rifiuta.
 * Il reset annuale nasce spento: accenderlo richiede un anno nel pattern, e
 * proporlo acceso su un pattern senza anno mostrerebbe subito un errore.
 */
export function defaultConfiguration(): RegistryConfiguration {
  return {
    pattern: { ...DEFAULT_PATTERN },
    initial: 1,
    annualReset: false,
  };
}

/** La configurazione di un registro esistente, per aprirla in modifica. */
export function configurationOf(registry: RegistryRead): RegistryConfiguration {
  return {
    pattern: { ...registry.configuration.pattern },
    initial: registry.configuration.initial,
    annualReset: registry.configuration.annualReset,
  };
}

/**
 * I registri che un campo di **questo** modello può scegliere.
 *
 * Sono due cose diverse, e la tendina le deve tenere distinte:
 *
 * - quelli **dell'organizzazione**, che valgono per tutti i modelli e si
 *   possono sempre recuperare;
 * - quelli **locali di questo modello**, che sono i suoi contatori e non si
 *   condividono con nessun altro.
 *
 * Quelli locali di *un altro* modello non si offrono: sceglierne uno farebbe
 * emettere due modelli dallo stesso contatore credendoli indipendenti — la
 * confusione che il registro locale esiste per evitare. Il server la vede
 * (`SHARED_REGISTRY`) e la fa confermare, ma offrirla sarebbe suggerirla.
 *
 * Il registro **già scelto** resta visibile anche se non fosse selezionabile:
 * nasconderlo mostrerebbe una tendina vuota su un campo che ha un valore, e
 * non si potrebbe più correggere.
 *
 * Un registro **già preso da un altro campo** dello stesso modello non si offre
 * (`usedRegistryIds`): due campi `autoincrement` sullo stesso contatore si
 * ruberebbero i numeri a vicenda — è la stessa confusione che il registro locale
 * esiste per evitare, solo dentro un modello invece di fra modelli. Come sopra,
 * il registro già scelto dal campo che si sta configurando resta visibile anche
 * se risulta preso (da sé stesso): altrimenti lo si perderebbe senza che
 * l'utente abbia toccato niente.
 */
export function selectableRegistries(
  registries: readonly RegistryRead[],
  options: {
    modelId?: string | null;
    selectedId?: string | null;
    /** I registri già scelti da altri campi del modello. */
    usedRegistryIds?: readonly string[];
  } = {}
): RegistryRead[] {
  const modello = String(options.modelId ?? "");
  const presi = options.usedRegistryIds ?? [];
  return registries.filter((registry) => {
    if (
      registry.registryId !== options.selectedId &&
      presi.includes(registry.registryId)
    ) {
      return false;
    }
    if (registry.scope === "organization") return true;
    if (String(registry.modelId ?? "") === modello && modello !== "") return true;
    return registry.registryId === options.selectedId;
  });
}

/**
 * I registri che un pannello amministra.
 *
 * Diverso da `selectableRegistries`, e la differenza **è** il punto dei due
 * livelli: un campo può scegliere i registri dell'organizzazione *e* i propri
 * del modello, mentre un pannello amministra solo quelli del suo livello — le
 * impostazioni dell'azienda non sono il posto in cui spegnere il contatore
 * locale di un modello, né viceversa.
 *
 * Il modello si confronta **fra stringhe**: il server lo restituisce ora numero
 * ora stringa, e un `===` fra i due tipi direbbe sempre "diverso" — il registro
 * locale sparirebbe dal pannello che deve amministrarlo.
 */
export function registriesOfScope(
  registries: readonly RegistryRead[],
  identity: RegistryScope
): RegistryRead[] {
  return registries.filter((registry) =>
    identity.scope === "organization"
      ? registry.scope === "organization"
      : registry.scope === "model" &&
        String(registry.modelId ?? "") === String(identity.modelId ?? "")
  );
}

export interface RegistryDraftInput {
  /** Organizzazione, e per un registro locale il modello a cui appartiene. */
  identity: RegistryScope;
  /** Il registro in modifica; assente in creazione. */
  registry?: RegistryRead | null;
  code: string;
  name: string;
  configuration: RegistryConfiguration;
  /** Lo stato voluto. In creazione il registro nasce attivo. */
  active: boolean;
  operationId: string;
}

/**
 * I problemi da mostrare **prima** dell'invio, come chiavi di traduzione.
 *
 * Non è una seconda validazione: usa quella del contratto
 * (`registryConfigIssues`) e aggiunge l'unica cosa che il contratto non può
 * sapere, cioè se l'identità è stata compilata.
 */
export function draftIssues(input: {
  code: string;
  configuration: RegistryConfiguration;
  creating: boolean;
}): string[] {
  const issues = registryConfigIssues(input.configuration);

  if (input.creating) {
    const code = input.code.trim();
    if (code === "") {
      issues.push("custom_fields.error.validation_failed");
    } else if (code.length > PATTERN_LIMITS.code) {
      issues.push("custom_fields.error.invalid_range");
    }
  }

  return issues;
}

/**
 * Il corpo della creazione.
 *
 * Lo scope e il modello si mandano **solo qui**: è l'unico momento in cui
 * decidono qualcosa. Assenti significano organizzazione, che è il
 * comportamento che c'era prima dello scope.
 */
export function createPayload(input: RegistryDraftInput): RegistryMutation {
  return {
    operationId: input.operationId,
    // La creazione porta revisione zero: non c'è una precedente su cui
    // costruire, e dichiararne una sarebbe una richiesta di permesso su
    // qualcosa che non esiste.
    expectedRevision: 0,
    scope: input.identity.scope,
    ...(input.identity.scope === "model"
      ? { modelId: input.identity.modelId ?? null }
      : {}),
    code: input.code.trim(),
    name: effectiveName(input),
    configuration: input.configuration,
  };
}

/**
 * Il nome con cui il registro nascerà.
 *
 * Il nome è facoltativo, e quando manca è il codice. Sta qui e non nel
 * chiamante perché **il controllo di conflitto deve guardare lo stesso nome che
 * verrà scritto**: due espressioni della stessa regola divergono alla prima
 * modifica, e il conflitto sfuggirebbe proprio nel caso "nome vuoto".
 */
export const effectiveName = (input: { code: string; name: string }): string =>
  input.name.trim() || input.code.trim();

/**
 * Il registro che porta già questo nome.
 *
 * Il vincolo del contratto è sull'identità (`code`), e resta del server. Questo
 * è un'altra cosa: due contatori con lo stesso **nome** in due ambiti diversi —
 * uno d'organizzazione e uno di modello — sono la stessa confusione, perché chi
 * legge una tendina non sa quale dei due sta scegliendo. L'elenco che il server
 * restituisce è già quello dell'organizzazione intera (organizzazione e tutti i
 * modelli), quindi il confronto attraversa gli ambiti senza chiedere altro.
 *
 * Confronto su spazi rimossi e senza maiuscole: `"Protocollo "` e
 * `"protocollo"` sono lo stesso nome per chi legge, e due contatori che
 * differiscono solo per quello non si distinguono a occhio.
 */
export function nameConflict(
  name: string,
  registries: readonly RegistryRead[],
  options: { ignoreRegistryId?: string | null } = {}
): RegistryRead | null {
  const wanted = name.trim().toLocaleLowerCase();
  if (wanted === "") return null;

  return (
    registries.find(
      (registry) =>
        registry.registryId !== options.ignoreRegistryId &&
        registry.name.trim().toLocaleLowerCase() === wanted
    ) ?? null
  );
}

/**
 * Il registro che porta già questo codice.
 *
 * Il vincolo del contratto è sull'identità (`code`), ed è il server a
 * difenderlo: la creazione di un codice già preso nell'organizzazione torna un
 * 409. Ma un 409 racconta il conflitto **dopo** l'invio, quando l'utente ha già
 * creduto di aver finito; qui lo si vede prima, mentre il dialogo è ancora
 * aperto e il campo si può correggere.
 *
 * È la gemella di `nameConflict`, e come quella guarda l'elenco intero
 * dell'organizzazione — organizzazione e modelli — perché è quello che il
 * server restituisce, e il codice è unico nello stesso insieme.
 *
 * Confronto su spazi rimossi e senza maiuscole, come per il nome: due codici
 * che differiscono solo per quello sono lo stesso codice per chi legge.
 * `ignoreRegistryId` serve al caso in cui non si sta creando: il registro in
 * modifica non è in conflitto con sé stesso.
 */
export function codeConflict(
  code: string,
  registries: readonly RegistryRead[],
  options: { ignoreRegistryId?: string | null } = {}
): RegistryRead | null {
  const wanted = code.trim().toLocaleLowerCase();
  if (wanted === "") return null;

  return (
    registries.find(
      (registry) =>
        registry.registryId !== options.ignoreRegistryId &&
        registry.code.trim().toLocaleLowerCase() === wanted
    ) ?? null
  );
}

/**
 * Il corpo della modifica.
 *
 * Niente `code` e niente scope: la configurazione cambia, l'identità no. Lo
 * stato si manda sempre, così una disattivazione non è un effetto collaterale
 * di un salvataggio che parlava d'altro.
 */
export function updatePayload(input: RegistryDraftInput): RegistryMutation {
  const registry = input.registry;
  if (!registry) {
    throw new Error("updatePayload richiede il registro da modificare");
  }
  return {
    operationId: input.operationId,
    expectedRevision: registry.revision,
    configuration: input.configuration,
    active: input.active,
  };
}
